// src/components/DocumentPreviewModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const DocumentPreviewModal = ({ isOpen, onClose, file, title }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Reset loading state when file changes
  useEffect(() => {
    if (file) {
      setIsLoading(true);
      setError(null);
    }
  }, [file]);

  if (!isOpen || !file) return null;

  const getFileUrl = (file) => {
    if (!file) return null;

    // Handle different file object structures
    if (file.file_url) return file.file_url;
    if (file.fileUrl) return file.fileUrl;
    if (file.url) return file.url;
    if (file.file_path) {
      // If it's a relative path, construct the full URL
      if (file.file_path.startsWith('http')) {
        return file.file_path;
      }
      return `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${file.file_path}`;
    }
    return null;
  };

  const url = getFileUrl(file);
  const fileName = file.file_name || file.fileName || file.name || 'Document';
  const isPDF = fileName.toLowerCase().endsWith('.pdf') ||
                file.mime_type?.includes('pdf') ||
                file.mimeType?.includes('pdf') ||
                url?.toLowerCase().includes('.pdf');

  const handleLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleError = () => {
    setIsLoading(false);
    setError('Failed to load document. Please try opening in a new tab.');
  };

  const handleOpenInNewTab = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownload = () => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        <div
          className="bg-white rounded-lg sm:rounded-2xl shadow-2xl w-full h-full sm:w-[96vw] sm:h-[96vh] lg:w-[90vw] lg:h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-secondary-200 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={18} className="text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-lg font-semibold text-text-primary truncate">
                  {title || fileName}
                </h3>
                <p className="text-xs text-text-secondary truncate hidden sm:block">{fileName}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2 sm:ml-4">
              <button
                onClick={handleDownload}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-text-primary bg-secondary-100 rounded-lg hover:bg-secondary-200 transition-colors"
                title="Download"
              >
                <Icon name="Download" size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden md:inline">Download</span>
              </button>
              <button
                onClick={handleOpenInNewTab}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                title="Open in new tab"
              >
                <Icon name="ExternalLink" size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden md:inline">New Tab</span>
              </button>
              <button
                onClick={onClose}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors"
                title="Close"
              >
                <Icon name="X" size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 relative overflow-hidden bg-secondary-50">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                  <p className="text-sm text-text-secondary">Loading document...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
                    <Icon name="AlertCircle" size={32} className="text-danger-600" />
                  </div>
                  <p className="text-text-primary font-medium mb-2">Unable to preview document</p>
                  <p className="text-sm text-text-secondary mb-4">{error}</p>
                  <button
                    onClick={handleOpenInNewTab}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Open in New Tab
                  </button>
                </div>
              </div>
            )}

            {url && (
              <>
                {isPDF ? (
                  <iframe
                    src={`${url}#view=FitH`}
                    className="w-full h-full border-0"
                    title={fileName}
                    onLoad={handleLoad}
                    onError={handleError}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center p-8">
                    <img
                      src={url}
                      alt={fileName}
                      className="max-w-full max-h-full object-contain"
                      onLoad={handleLoad}
                      onError={handleError}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer Info */}
          <div className="p-2 sm:p-3 border-t border-secondary-200 bg-secondary-50 flex-shrink-0">
            <div className="flex items-center justify-between text-xs text-text-secondary">
              <span className="hidden sm:inline">
                Press <kbd className="px-2 py-1 bg-white border border-secondary-200 rounded text-[10px]">ESC</kbd> to close
              </span>
              <span className="sm:hidden text-[10px] text-text-tertiary">
                Tap outside to close
              </span>
              <span className="text-[10px] sm:text-xs text-text-tertiary">
                {isPDF ? 'PDF Document' : 'Image Document'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentPreviewModal;
